import { type ApiErrorCode, apiErrorSchema } from '@ofmos/shared/api-error';

/**
 * The ONE place the browser talks to the API.
 *
 * Everything above this file (the metadata, record and auth clients) works with typed
 * results and `ApiError`; nothing above it sees `fetch`, a status code or a JSON envelope.
 *
 * Three rules live here because they must hold for every call:
 *  - `credentials: 'include'` - the session is an httpOnly cookie (cookies.ts), so a
 *    request without it is anonymous. Same-origin would send it anyway; saying so
 *    explicitly is what keeps a future cross-origin deployment from silently logging
 *    everybody out.
 *  - a non-2xx response becomes an `ApiError` carrying the SERVER's machine-readable code,
 *    not a string a screen has to regex. That is what lets the UI tell "confirm this" from
 *    "you may not" from "we are down".
 *  - a 401 is reported once to the session layer (`onUnauthenticated`) and then thrown.
 *    Session expiry is not something 40 call sites can each remember to handle.
 */

export interface ApiErrorInit {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly details?: unknown;
}

export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly details: unknown;

  constructor(message: string, init: ApiErrorInit) {
    super(message);
    this.code = init.code;
    this.status = init.status;
    this.details = init.details;
  }

  /** INV-8: a refused destructive change hands back the token that would allow it. */
  get confirmToken(): string | undefined {
    if (typeof this.details !== 'object' || this.details === null) return undefined;
    const token = (this.details as { confirmToken?: unknown }).confirmToken;
    return typeof token === 'string' ? token : undefined;
  }
}

/** The network itself failed: no status, no envelope, nothing to map. */
export class NetworkError extends Error {
  override readonly name = 'NetworkError';
}

export interface TransportOptions {
  /** Prefix for every path. Empty in the app: the API is same-origin behind Caddy/Vite. */
  readonly baseUrl?: string;
  /** Called once per 401, before the error is thrown. */
  readonly onUnauthenticated?: (() => void) | undefined;
}

export interface RequestOptions {
  readonly method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly path: string;
  readonly body?: unknown;
  readonly query?: Readonly<Record<string, string | number | undefined>>;
  /** Treat a 401 as a plain result instead of an error. Only `GET me` wants this. */
  readonly allowUnauthenticated?: boolean;
  /**
   * A body that is NOT JSON, sent verbatim under its own content type. TWO routes in the
   * product want this, and both are routes whose whole payload is a file:
   * `PUT /api/imports/:id/file` (`csv-import-export.md` D3) and
   * `PUT /api/files/:id/content` (`file-attachments.md` D3). Each answers a raw parser
   * behind its own body limit, deliberately not multipart. Mutually exclusive with `body`.
   *
   * `body` WIDENED FROM `string` TO `string | Blob | ArrayBuffer` for the second of them,
   * and that widening is the whole client-side change the upload needed. The CSV path only
   * ever worked because its stage does `await file.text()`; a 25 MB PDF cannot survive
   * that. `send` already hands this straight to `fetch`, which takes all three - so the
   * upload keeps `credentials: 'include'`, `toApiError` and the 401 hook rather than
   * growing a second HTTP client that would have to re-implement them (obligation 50).
   */
  readonly rawBody?:
    | { readonly contentType: string; readonly body: string | Blob | ArrayBuffer }
    | undefined;
}

function queryString(query: RequestOptions['query']): string {
  if (query === undefined) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const rendered = params.toString();
  return rendered === '' ? '' : `?${rendered}`;
}

/**
 * Turns a failed response into an `ApiError`.
 *
 * The body is parsed against the SHARED envelope, so a proxy error page (HTML, or a 502
 * from Caddy) cannot be mistaken for an API refusal - it becomes an honest `internal`
 * with the status attached.
 */
async function toApiError(response: Response): Promise<ApiError> {
  const fallbackCode: ApiErrorCode = response.status >= 500 ? 'internal' : 'invalid_request';
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    return new ApiError(`The API answered ${response.status}.`, {
      code: fallbackCode,
      status: response.status,
    });
  }
  const parsed = apiErrorSchema.safeParse(payload);
  if (!parsed.success) {
    return new ApiError(`The API answered ${response.status} with an unexpected body.`, {
      code: fallbackCode,
      status: response.status,
    });
  }
  return new ApiError(parsed.data.error.message, {
    code: parsed.data.error.code,
    status: response.status,
    details: parsed.data.error.details,
  });
}

export interface Transport {
  request<T>(options: RequestOptions): Promise<T>;
  /** `null` for a 401 when `allowUnauthenticated` is set. */
  requestMaybe<T>(options: RequestOptions): Promise<T | null>;
  /**
   * The body as TEXT, for the three routes that answer with a file rather than with JSON:
   * `GET /api/exports/:id/file`, `GET /api/imports/:id/errors.csv` and
   * `GET /api/imports/:id/report.csv` (`csv-import-export.md` D13, D34, D44).
   *
   * A bare `<a download>` was the alternative and is worse in three ways that all matter
   * here: it leaves the transport, so a 403 renders the error envelope as a page instead
   * of raising `ApiError`; it cannot report a refusal to the screen that asked; and under
   * the fixtures build there is no server behind it at all. This keeps the cookie, the
   * error mapping and the 401 hook that every other call has, and the caller turns the
   * string into a file (`app/csv/download.ts`).
   */
  requestText(options: RequestOptions): Promise<string>;
  /**
   * The body as a BLOB, for the one route that answers with bytes rather than with text:
   * `GET /api/files/:id/content` (`file-attachments.md` D5).
   *
   * Beside `requestText` rather than instead of it, because the two differ in exactly one
   * line and agree on everything that matters: the cookie, the error mapping and the 401
   * hook. A CSV is text and a PDF is not, and `response.text()` on 25 MB of PDF produces a
   * string of replacement characters that looks like a successful download.
   *
   * A bare `<a href download>` is the alternative and is refused for the reason
   * `app/csv/download.ts` already writes down: it leaves the transport, so a 403 renders
   * the error envelope as a page and the screen that asked cannot report the refusal.
   */
  requestBlob(options: RequestOptions): Promise<Blob>;
}

export function createTransport(options: TransportOptions = {}): Transport {
  const baseUrl = options.baseUrl ?? '';

  const send = async (request: RequestOptions, accept: string): Promise<Response> => {
    const url = `${baseUrl}${request.path}${queryString(request.query)}`;
    const contentType =
      request.rawBody !== undefined
        ? request.rawBody.contentType
        : request.body === undefined
          ? undefined
          : 'application/json';
    const body =
      request.rawBody !== undefined
        ? request.rawBody.body
        : request.body === undefined
          ? undefined
          : JSON.stringify(request.body);
    try {
      return await fetch(url, {
        method: request.method,
        credentials: 'include',
        headers: contentType === undefined ? { accept } : { accept, 'content-type': contentType },
        ...(body === undefined ? {} : { body }),
      });
    } catch (cause) {
      throw new NetworkError(`The server could not be reached (${String(cause)}).`);
    }
  };

  const run = async <T>(request: RequestOptions): Promise<T | null> => {
    const response = await send(request, 'application/json');

    if (response.status === 401) {
      options.onUnauthenticated?.();
      if (request.allowUnauthenticated === true) return null;
    }
    if (!response.ok) throw await toApiError(response);
    // 204: a successful call with nothing to say (logout, delete).
    if (response.status === 204) return null;
    return (await response.json()) as T;
  };

  return {
    request: async <T>(request: RequestOptions): Promise<T> => (await run<T>(request)) as T,
    requestMaybe: <T>(request: RequestOptions): Promise<T | null> => run<T>(request),
    requestText: async (request: RequestOptions): Promise<string> => {
      /**
       * `accept: text/csv` and NOT `application/json`, because a refusal on these routes
       * still arrives as the JSON envelope: `toApiError` parses it either way, so the
       * failure path is identical to every other call's and only the success path differs.
       */
      const response = await send(request, 'text/csv');
      if (response.status === 401) options.onUnauthenticated?.();
      if (!response.ok) throw await toApiError(response);
      return response.text();
    },
    requestBlob: async (request: RequestOptions): Promise<Blob> => {
      /**
       * A WILDCARD `accept` and not a type: the caller does not know what the file IS - that
       * is the server's answer, sniffed from the bytes (D4) - and naming one here would be a
       * second opinion about it. A refusal still arrives as the JSON envelope, so
       * `toApiError` parses it either way and only the success path differs.
       */
      const response = await send(request, '*/*');
      if (response.status === 401) options.onUnauthenticated?.();
      if (!response.ok) throw await toApiError(response);
      return response.blob();
    },
  };
}
